/**
 * Suggested starts above Home's composer (homeSuggestions.logic).
 * Picking one fills the composer with its ask and focuses it; the person reads,
 * edits and sends it. They fade while the composer has text and come back
 * when it is cleared, keeping their room so the centred page never moves.
 *
 * @module components/home/HomeSuggestions
 */
import { ClipboardCheckIcon, NewspaperIcon, WorkflowIcon } from "lucide-react";

import { cn } from "~/lib/utils";
import { SeverityGlyph } from "../regulation/finding/findingGlyphs";
import { SuggestionChip } from "../ui/suggestion-chip";
import { Tip } from "../ui/tip";
import type { Suggestion } from "./homeSuggestions.logic";

function SuggestionIcon({ suggestion }: { readonly suggestion: Suggestion }) {
  switch (suggestion.kind) {
    case "finding":
      return <SeverityGlyph severity={suggestion.severity} tone="quiet" className="size-3.5" />;
    case "update":
      return <NewspaperIcon className="text-muted-foreground" />;
    case "workflow":
      return <WorkflowIcon className="text-muted-foreground" />;
    case "review":
      return <ClipboardCheckIcon className="text-muted-foreground" />;
  }
}

export function HomeSuggestions({
  suggestions,
  hidden,
  onPick,
}: {
  readonly suggestions: ReadonlyArray<Suggestion>;
  /** The composer has text: fade out, keeping the room. */
  readonly hidden: boolean;
  /** Fills the composer with the ask; never sends it. */
  readonly onPick: (prompt: string) => void;
}) {
  if (suggestions.length === 0) return null;
  return (
    <div
      className={cn(
        "transition-opacity duration-200 ease-in-out motion-reduce:transition-none",
        hidden ? "pointer-events-none opacity-0" : "opacity-100",
      )}
      aria-hidden={hidden || undefined}
      inert={hidden}
    >
      {/*
        One row from sm: each chip gets its whole label while the row has room,
        and the long ones share what is left evenly and truncate. On a phone
        they wrap, each at most the row's width.
      */}
      <div className="mb-2 flex flex-wrap gap-x-2.5 gap-y-2 sm:grid sm:auto-cols-[minmax(0,max-content)] sm:grid-flow-col">
        {suggestions.map((suggestion) => (
          <Tip key={suggestion.key} label={`${suggestion.context}\n\n${suggestion.prompt}`}>
            <SuggestionChip
              icon={<SuggestionIcon suggestion={suggestion} />}
              label={suggestion.label}
              onClick={() => onPick(suggestion.prompt)}
            />
          </Tip>
        ))}
      </div>
    </div>
  );
}
